/**
 * Newsletter List Sync Service
 *
 * Sincronizza i clienti/lead con le liste newsletter dinamiche.
 * Quando un client viene creato o modificato, questo servizio valuta
 * quali liste newsletter matchano e crea record in newsletter_subscribers.
 *
 * Il trigger database su newsletter_subscribers poi attiva i workflow
 * tramite l'edge function trigger-workflow.
 */

import { supabase } from '../supabase';
import { NewsletterList, NewsletterListFilters } from '../../types';
import { workflowExecutionService } from './workflowExecutionService';

interface ClientData {
  id: string;
  email?: string | null;
  newsletter_email?: string | null;
  phone?: string | null;
  whatsapp_number?: string | null;
  business_name?: string | null;
  contact_name?: string | null;
  stato_cliente?: string | null;
  entity_type?: string | null;
  lead_source?: string | null;
  country?: string | null;
  newsletter_subscribed?: boolean;
  organization_id: string;
}

interface SyncResult {
  listsMatched: number;
  subscribersCreated: number;
  subscribersRemoved: number;
  errors: string[];
}

class NewsletterListSyncService {
  /**
   * Sincronizza un client/lead con tutte le liste newsletter.
   * Crea record in newsletter_subscribers per le liste che matchano.
   */
  async syncClientToLists(
    clientId: string,
    organizationId: string,
    isLead: boolean
  ): Promise<SyncResult> {
    const result: SyncResult = {
      listsMatched: 0,
      subscribersCreated: 0,
      subscribersRemoved: 0,
      errors: [],
    };

    try {
      console.log(`📧 [NewsletterListSync] Syncing client ${clientId} to newsletter lists`);

      // 1. Recupera i dati del client
      const { data: clientData, error: clientError } = await supabase
        .from('clients')
        .select('id, email, newsletter_email, phone, whatsapp_number, business_name, contact_name, stato_cliente, entity_type, lead_source, country, newsletter_subscribed, organization_id')
        .eq('id', clientId)
        .eq('organization_id', organizationId)
        .single();

      if (clientError || !clientData) {
        console.error(`[NewsletterListSync] Client not found: ${clientId}`);
        result.errors.push(`Client not found: ${clientId}`);
        return result;
      }

      // 2. Recupera i tag del client
      const { data: tagAssignments } = await supabase
        .from('client_tag_assignments')
        .select('tag_id')
        .eq('client_id', clientId)
        .eq('organization_id', organizationId);

      const clientTagIds = tagAssignments?.map(t => t.tag_id) || [];

      // 3. Recupera le categorie di interesse del client
      const { data: interests } = await supabase
        .from('client_interests')
        .select('interest_category_id')
        .eq('client_id', clientId)
        .eq('organization_id', organizationId)
        .eq('status', 'active');

      const clientCategoryIds = interests?.map(i => i.interest_category_id) || [];

      // 4. Recupera tutte le liste newsletter e whatsapp attive
      const { data: lists, error: listsError } = await supabase
        .from('newsletter_lists')
        .select('*')
        .eq('organization_id', organizationId)
        .in('campaign_type', ['newsletter', 'whatsapp']);

      if (listsError || !lists) {
        console.error('[NewsletterListSync] Error fetching lists:', listsError);
        result.errors.push('Error fetching newsletter lists');
        return result;
      }

      console.log(`[NewsletterListSync] Found ${lists.length} newsletter lists to check`);

      // 5. Per ogni lista, verifica se il client matcha i filtri
      for (const list of lists) {
        try {
          const matches = await this.clientMatchesListFilters(
            clientData,
            list.filters as NewsletterListFilters,
            isLead,
            clientTagIds,
            clientCategoryIds
          );

          if (matches) {
            result.listsMatched++;

            // Verifica se esiste già un subscriber per questa combinazione
            const { data: existing } = await supabase
              .from('newsletter_subscribers')
              .select('id')
              .eq('newsletter_list_id', list.id)
              .eq('entity_id', clientId)
              .eq('organization_id', organizationId)
              .maybeSingle();

            if (!existing) {
              // Determina l'email da usare
              const email = clientData.newsletter_email || clientData.email;

              if (!email) {
                console.log(`[NewsletterListSync] Client ${clientId} has no email, skipping list ${list.name}`);
                continue;
              }

              // Crea il record subscriber - il trigger DB scatterà automaticamente
              const { error: insertError } = await supabase
                .from('newsletter_subscribers')
                .insert({
                  newsletter_list_id: list.id,
                  email: email,
                  name: clientData.contact_name || clientData.business_name || null,
                  entity_type: isLead ? 'lead' : 'client',
                  entity_id: clientId,
                  phone: clientData.phone || clientData.whatsapp_number || null,
                  organization_id: organizationId,
                  status: 'subscribed',
                  subscribed: true,
                });

              if (insertError) {
                // Ignora errori di duplicazione (già iscritto)
                if (insertError.code !== '23505') {
                  console.error(`[NewsletterListSync] Error inserting subscriber:`, insertError);
                  result.errors.push(`Error subscribing to list ${list.name}: ${insertError.message}`);
                }
              } else {
                result.subscribersCreated++;
                console.log(`✅ [NewsletterListSync] Added ${clientId} to list "${list.name}"`);

                // Trigger workflow "contact_added_to_list" (come fanno tutti gli altri trigger)
                try {
                  await workflowExecutionService.triggerWorkflows({
                    entityType: 'newsletter',
                    triggerType: 'contact_added_to_list',
                    entityId: clientId,
                    triggerData: {
                      list_id: list.id,
                      list_name: list.name,
                      subscriber_email: email,
                      entity_type: isLead ? 'lead' : 'client',
                    },
                    triggerConfig: { list_id: list.id }
                  });
                  console.log(`🚀 [NewsletterListSync] Triggered workflow for list "${list.name}"`);
                } catch (workflowError) {
                  console.error(`[NewsletterListSync] Error triggering workflow:`, workflowError);
                  // Non bloccare il flusso se il workflow fallisce
                }
              }
            } else {
              console.log(`[NewsletterListSync] Client ${clientId} already in list "${list.name}"`);
            }
          }
        } catch (listError) {
          console.error(`[NewsletterListSync] Error processing list ${list.id}:`, listError);
          result.errors.push(`Error processing list ${list.name}`);
        }
      }

      // 6. Recupera le iscrizioni attuali del client per verificare rimozioni
      const { data: currentSubscriptions } = await supabase
        .from('newsletter_subscribers')
        .select('id, newsletter_list_id, email')
        .eq('entity_id', clientId)
        .eq('organization_id', organizationId)
        .eq('status', 'subscribed');

      // 7. Per ogni iscrizione, verifica se il client matcha ancora i filtri
      for (const subscription of currentSubscriptions || []) {
        const list = lists.find(l => l.id === subscription.newsletter_list_id);

        if (!list) continue; // Lista non trovata o non più attiva

        const stillMatches = await this.clientMatchesListFilters(
          clientData,
          list.filters as NewsletterListFilters,
          isLead,
          clientTagIds,
          clientCategoryIds
        );

        if (!stillMatches) {
          // Rimuovi dalla lista
          const { error: deleteError } = await supabase
            .from('newsletter_subscribers')
            .delete()
            .eq('id', subscription.id);

          if (!deleteError) {
            result.subscribersRemoved++;
            console.log(`🚫 [NewsletterListSync] Removed ${clientId} from list "${list.name}" (no longer matches filters)`);

            // Trigger workflow "unsubscribe"
            try {
              await workflowExecutionService.triggerWorkflows({
                entityType: 'newsletter',
                triggerType: 'unsubscribe',
                entityId: clientId,
                triggerData: {
                  list_id: list.id,
                  list_name: list.name,
                  subscriber_email: subscription.email,
                  entity_type: isLead ? 'lead' : 'client',
                  reason: 'no_longer_matches_filters',
                },
                triggerConfig: { list_id: list.id }
              });
              console.log(`🚀 [NewsletterListSync] Triggered unsubscribe workflow for list "${list.name}"`);
            } catch (workflowError) {
              console.error(`[NewsletterListSync] Error triggering unsubscribe workflow:`, workflowError);
            }
          } else {
            console.error(`[NewsletterListSync] Error removing subscriber:`, deleteError);
            result.errors.push(`Error removing from list ${list.name}: ${deleteError.message}`);
          }
        }
      }

      console.log(`📧 [NewsletterListSync] Sync complete: ${result.listsMatched} lists matched, ${result.subscribersCreated} created, ${result.subscribersRemoved} removed`);

      return result;
    } catch (error) {
      console.error('[NewsletterListSync] Sync error:', error);
      result.errors.push((error as Error).message);
      return result;
    }
  }

  /**
   * Verifica se un client matcha i filtri di una lista newsletter
   */
  private async clientMatchesListFilters(
    client: ClientData,
    filters: NewsletterListFilters,
    isLead: boolean,
    clientTagIds: string[],
    clientCategoryIds: string[]
  ): Promise<boolean> {
    // 0. Guardia referenti: una persona-referente (individual + stato_cliente NULL) NON è
    //    un lead/cliente e non deve entrare nel cache subscribers delle liste leads/clients.
    //    Allineato a EXCLUDE_REFERENTI e alla guardia nei resolver queue_campaign_*.
    if (client.entity_type === 'individual' && !client.stato_cliente) {
      return false;
    }

    // 1. Verifica entity_types
    const entityType = isLead ? 'leads' : 'clients';
    if (!filters.entity_types || filters.entity_types.length === 0) {
      return false; // Nessun tipo di entità selezionato
    }
    if (!filters.entity_types.includes(entityType)) {
      return false; // Il tipo di entità non matcha
    }

    // 2. Verifica newsletter_consent
    const consent = filters.newsletter_consent || 'both';
    if (consent === 'yes' && !client.newsletter_subscribed) {
      return false;
    }
    if (consent === 'no' && client.newsletter_subscribed) {
      return false;
    }

    // 3. Verifica tags (se specificati)
    if (filters.tags && filters.tags.length > 0) {
      const hasMatchingTag = filters.tags.some(tagId => clientTagIds.includes(tagId));
      if (!hasMatchingTag) {
        return false;
      }
    }

    // 4. Verifica macro_categories (se specificati)
    if (filters.macro_categories && filters.macro_categories.length > 0) {
      const hasMatchingCategory = filters.macro_categories.some(catId => clientCategoryIds.includes(catId));
      if (!hasMatchingCategory) {
        return false;
      }
    }

    // 5. Verifica micro_categories (se specificati)
    if (filters.micro_categories && filters.micro_categories.length > 0) {
      const hasMatchingCategory = filters.micro_categories.some(catId => clientCategoryIds.includes(catId));
      if (!hasMatchingCategory) {
        return false;
      }
    }

    // 6. Verifica sources (se specificati)
    if (filters.sources && filters.sources.length > 0) {
      if (!client.lead_source || !filters.sources.includes(client.lead_source)) {
        return false;
      }
    }

    // 7. Verifica countries (se specificati)
    if (filters.countries && filters.countries.length > 0) {
      if (!client.country || !filters.countries.includes(client.country)) {
        return false;
      }
    }

    // Tutti i filtri passati!
    return true;
  }

  /**
   * Sincronizza un client in background (fire-and-forget)
   * Utile per non bloccare l'utente durante la creazione/modifica
   */
  syncClientToListsAsync(
    clientId: string,
    organizationId: string,
    isLead: boolean
  ): void {
    // Esegui in background senza attendere
    this.syncClientToLists(clientId, organizationId, isLead)
      .then(result => {
        if (result.errors.length > 0) {
          console.warn('[NewsletterListSync] Background sync completed with errors:', result.errors);
        }
      })
      .catch(error => {
        console.error('[NewsletterListSync] Background sync failed:', error);
      });
  }
}

export const newsletterListSyncService = new NewsletterListSyncService();
